import { formatarDataHora, formatarInteiro, formatarReais } from '@/lib/formato';

import type { AjusteDaApi } from './api';

/**
 * Os textos dos pedidos de ajuste (0059) — correção de lançamento e balanço do
 * tanque —, fora do componente para o teste os ler sem montar tela.
 */

const ROTULO_DO_TIPO_DE_LANCAMENTO: Readonly<Record<string, string>> = {
  recebimento: 'Recebimento',
  saida: 'Saída para abastecimento',
  transferencia_entrada: 'Entrada por transferência',
  transferencia_saida: 'Saída por transferência',
  ajuste_entrada: 'Ajuste de entrada',
  ajuste_saida: 'Ajuste de saída',
};

export function rotuloDoLancamento(tipo: string): string {
  return ROTULO_DO_TIPO_DE_LANCAMENTO[tipo] ?? tipo;
}

/** A situação escrita, na grafia da tela. "aplicando" só existe dentro do banco. */
export function situacaoDoAjuste(situacao: AjusteDaApi['situacao']): string {
  if (situacao === 'aguardando aprovacao' || situacao === 'aplicando') return 'Aguardando aprovação';
  if (situacao === 'aprovado') return 'Aprovado';
  if (situacao === 'recusado') return 'Recusado';
  return 'Cancelado';
}

/** Litros com sinal: o que o ajuste faz no volume do tanque. */
export function litrosComSinal(valor: number): string {
  if (valor === 0) return '0\u00a0L';
  return `${valor > 0 ? '+' : '−'}${formatarInteiro(Math.abs(valor))}\u00a0L`;
}

/**
 * O que o pedido pede, em uma frase. É o que quem aprova lê primeiro, então
 * ela diz o lançamento e o valor, e não só "correção".
 */
export function oQueOAjustePede(a: AjusteDaApi): string {
  if (a.tipo === 'balanco') {
    const quando = a.contadoEm === null ? '' : ` em ${formatarDataHora(new Date(a.contadoEm))}`;
    return `Balanço: contados ${formatarInteiro(a.volumeContadoL ?? 0)}\u00a0L${quando}, o volume lançado era ${formatarInteiro(a.volumeDoLivroL ?? 0)}\u00a0L`;
  }
  const lancamento = rotuloDoLancamento(a.movimentoTipo ?? '');
  const de = formatarInteiro(a.movimentoQuantidadeL ?? 0);
  if ((a.quantidadeCorretaL ?? 0) === 0) return `Anular ${lancamento.toLowerCase()} de ${de}\u00a0L`;
  return `Corrigir ${lancamento.toLowerCase()} de ${de}\u00a0L para ${formatarInteiro(a.quantidadeCorretaL ?? 0)}\u00a0L`;
}

/** O valor que a alçada mede. Sem preço no tanque, a frase diz o que isso implica. */
export function valorDoAjuste(a: AjusteDaApi): string {
  if (a.valorEstimado === null) return 'Sem preço no tanque: só alçada sem teto aprova';
  return formatarReais(a.valorEstimado);
}

/**
 * O que a pessoa pode fazer com o pedido, e por que não, quando não pode. O
 * banco confere tudo de novo; isto só evita oferecer o botão que vai ser
 * recusado.
 */
export function oQueEuPossoFazer(a: AjusteDaApi): {
  readonly decidir: boolean;
  readonly cancelar: boolean;
  readonly porQueNao: string | null;
} {
  if (a.situacao !== 'aguardando aprovacao') return { decidir: false, cancelar: false, porQueNao: null };
  if (a.meu) {
    return { decidir: false, cancelar: true, porQueNao: 'Você pediu: outra pessoa com alçada de ajuste aprova.' };
  }
  if (!a.cabeNaMinhaAlcada) {
    return { decidir: false, cancelar: false, porQueNao: 'Fora da sua alçada de ajuste.' };
  }
  return { decidir: true, cancelar: false, porQueNao: null };
}
